import type { UserVehicle } from '@/types'
import type { CheckoutVehicleInfoValues } from '@/types/reservation-checkout'
import type { UpdateReservationContactInfoMutationInput } from '@/types/reservation-checkout'
import { VehicleEntryMode } from '@/types'

export const VEHICLE_INFO_FIELDS = [
    'number_plate',
    'car_color',
] as const satisfies ReadonlyArray<keyof CheckoutVehicleInfoValues>

export type CheckoutVehicleInfoField = (typeof VEHICLE_INFO_FIELDS)[number]

export function hasMissingVehicleNumberPlate(values: CheckoutVehicleInfoValues): boolean {
    return values.number_plate.trim() === ''
}

export function isVehicleInfoEmpty(values: CheckoutVehicleInfoValues): boolean {
    return values.number_plate.trim() === '' && values.car_color.trim() === ''
}

export type CheckoutVehicleSelection = 'custom' | `saved-car:${number}`

export function shouldShowVehicleEditor({
    values,
    edit,
    missingPlateDismissed,
}: {
    values: CheckoutVehicleInfoValues
    edit: boolean
    missingPlateDismissed: boolean
}): boolean {
    return edit || (hasMissingVehicleNumberPlate(values) && !missingPlateDismissed)
}

export function getSavedCarSelectionValue(carId: number): CheckoutVehicleSelection {
    return `saved-car:${carId}`
}

export function getInitialVehicleSelection({
    values,
    currentUserCars,
    shouldPrefillCheckoutInfo,
}: {
    values: CheckoutVehicleInfoValues
    currentUserCars: UserVehicle[]
    shouldPrefillCheckoutInfo: boolean
}): CheckoutVehicleSelection {
    if (currentUserCars.length === 0) {
        return 'custom'
    }

    const matchingCar = currentUserCars.find(car => car.number_plate === values.number_plate)

    if (matchingCar) {
        return getSavedCarSelectionValue(matchingCar.id)
    }

    if (
        values.number_plate.trim() !== ''
        || values.car_color.trim() !== ''
        || !shouldPrefillCheckoutInfo
    ) {
        return 'custom'
    }

    return getSavedCarSelectionValue(currentUserCars[0].id)
}

export function getSelectedSavedCar({
    currentUserCars,
    selection,
}: {
    currentUserCars: UserVehicle[]
    selection: CheckoutVehicleSelection
}): UserVehicle | null {
    if (selection === 'custom') {
        return null
    }

    return currentUserCars.find(car => getSavedCarSelectionValue(car.id) === selection) ?? null
}

export function getSavedCarVehiclePayload(car: UserVehicle): CheckoutVehicleInfoValues {
    return {
        number_plate: car.number_plate.trim(),
        car_color: car.color.trim(),
    }
}

export function normalizeRequiredVehicleValue(value: string): string {
    return value.trim()
}

export function normalizeOptionalVehicleValue(value: string): string | null {
    const trimmedValue = value.trim()
    return trimmedValue === '' ? null : trimmedValue
}

export function buildVehiclePayload({
    values,
    vehicleEntryMode,
    selectedSavedCar,
}: {
    values: CheckoutVehicleInfoValues
    vehicleEntryMode: VehicleEntryMode
    selectedSavedCar: UserVehicle | null
}): UpdateReservationContactInfoMutationInput {
    if (vehicleEntryMode === VehicleEntryMode.later) {
        return {
            number_plate: null,
            car_color: null,
        }
    }

    if (selectedSavedCar) {
        const savedCarPayload = getSavedCarVehiclePayload(selectedSavedCar)

        return {
            number_plate: savedCarPayload.number_plate,
            car_color: savedCarPayload.car_color.trim() === '' ? null : savedCarPayload.car_color,
        }
    }

    return {
        number_plate: normalizeRequiredVehicleValue(values.number_plate),
        car_color: normalizeOptionalVehicleValue(values.car_color),
    }
}
